import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Icon, Link, Button } from '@resy/ui-kitchen';
import pinSVG from '../../../../../../../ui-kitchen/src/svg/icons/ic_location_20x20.svg';
import Close from '../../../../../../../ui-kitchen/src/svg/icons/x.svg';
import Info from '../../../../../../../ui-kitchen/src/svg/icons/ic_info-outline_20x20.svg';
import IconCalendarDay from '../../../../../../../ui-kitchen/src/components/IconCalendarDay';
import { parameterize } from '../../../../../../../ui-kitchen/src/utils';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import { COMING_SOON_STATUS, NOTIFY_STATUS } from '../../constants';
import { handleMktAnalytics } from '../../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../../AmexMarketingUtils/constants';
import AmexPlatinumReservationButtonListRefresh from '../AmexPlatinumReservationButtonListRefresh';
import './AmexPlatinumNightsCardRefresh.scss';

const AmexPlatinumNightsCardRefresh = (
  {
    Analytics,
    restaurant = {},
    index,
    loadNotifyModal,
    showAuthModal,
    comingSoonLabel,
    bookLabel,
    notifyLabel,
    matchMedia,
  }
) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (Object.keys(restaurant).length === 0) return null;

  const {
    cuisine,
    locationName,
    venueName,
    venueId,
    image,
    status,
    date,
    locationSlug,
    venueSlug,
  } = restaurant;
  const comingSoonStatus = status === COMING_SOON_STATUS;
  const notifyStatus = status === NOTIFY_STATUS;
  const CustomIcon = isExpanded ? Close : Info;

  const hideNotify = notifyStatus && restaurant?.isNotifyHidden;

  let bookingBricksLimit = 2;
  if (matchMedia?.lg) bookingBricksLimit = 3;
  if (matchMedia?.xl) bookingBricksLimit = 4;

  const renderButtons = () => (
    <AmexPlatinumReservationButtonListRefresh
      ariaDescribedBy={`cardTitle${index}`}
      activeNotifications={restaurant.activeNotifications}
      Analytics={Analytics}
      featureRecaptcha={restaurant.featureRecaptcha}
      reservations={restaurant.slots}
      venueId={restaurant.venueId}
      venueName={venueName}
      limit={bookingBricksLimit}
      limitSlots
      selectedDate={restaurant.date}
      selectedSeats={restaurant.partySize}
      loadNotifyModal={loadNotifyModal}
      availableServiceTypes={restaurant.availableServiceTypes}
      showAuthModal={showAuthModal}
    />
  );

  const query = {
    date: restaurant.date,
    seats: restaurant.partySize,
  };

  const getVenueLink = () => `cities/${locationSlug}/venues/${venueSlug}?${parameterize(query)}`;

  const renderLabel = () => {
    if (comingSoonStatus) return comingSoonLabel;
    if (notifyStatus) return notifyLabel;
    return bookLabel;
  };

  const sanitizedInfoString = `${renderLabel()}:  ${sanitizeString(restaurant?.revervationInfo)}`;
  const sanitizedNotifyMessage = sanitizeString(restaurant?.hideNotifyMessage);
  const sanitizedVenueInfo = sanitizeString(restaurant?.venueInfo);

  return (
    <div
      role="group"
      aria-labelledby={`cardTitle${index}`}
      className={cx('AmexPlatinumNightsCardRefresh', {
        expanded: isExpanded,
      })}
    >
      <div
        className="AmexPlatinumNightsCardRefresh__top"
        style={{
          backgroundImage: `url(${image})`,
        }}
      >
        <time dateTime={date} className="calendar-icon">
          <IconCalendarDay date={date} />
        </time>
        <div className="AmexPlatinumNightsCardRefresh__content">
          <div className="AmexPlatinumNightsCardRefresh__content--top">
            <h3
              id={`cardTitle${index}`}
              className="AmexPlatinumNightsCardRefresh__name"
            >
              <Link
                className="venue-name"
                target="_blank"
                rel="noopener noreferrer"
                href={getVenueLink()}
                key={`${venueName}-${venueId}`}
                aria-label={venueName}
                onClick={() => {
                  if (Analytics.event) {
                    const extraPrams = {
                      action_name: 'Platinum Nights Venue Click',
                      item_name: venueName,
                    };
                    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
                  }
                }}
              >
                {venueName}
              </Link>
              <span className="cuisine">
                {cuisine}
              </span>
            </h3>
            {restaurant?.venueInfo && (
              <Button
                variant="no-style"
                type="button"
                id={`cardCta${index}`}
                className="AmexPlatinumNightsCardRefresh__expand--button"
                aria-label={`Information on ${venueName}`}
                aria-controls={`cardContent${index}`}
                aria-expanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <span className="AmexPlatinumNightsCardRefresh__iconwrapper">
                  <CustomIcon className={cx('', [isExpanded ? 'icon-close' : 'icon-info'])} aria-hidden="true" />
                </span>
              </Button>
            )}
          </div>
          {restaurant?.venueInfo && (
            <div
              role="region"
              className="AmexPlatinumNightsCardRefresh__content--bottom"
              aria-labelledby={`cardCta${index}`}
              id={`cardContent${index}`}
              aria-hidden={!isExpanded}
            >
              <div className="AmexPlatinumNightsCardRefresh__description">
                <div className="AmexPlatinumNightsCardRefresh__description--wrapper">
                  <p
                    dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                      __html: sanitizedVenueInfo,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <p className="AmexPlatinumNightsCardRefresh__location">
          <span aria-hidden="true">
            <Icon component={pinSVG} name="location-pin" />
          </span>

          {locationName}
        </p>
      </div>
      <div className="AmexPlatinumNightsCardRefresh__bottom">
        {(restaurant?.revervationInfo && comingSoonStatus) && (
          <p
            className="AmexPlatinumNightsCardRefresh__footer"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizeString(sanitizedInfoString),
            }}
          />
        )}
        {!hideNotify && !comingSoonStatus && renderButtons()}
        {(notifyStatus && restaurant?.isNotifyHidden && restaurant?.hideNotifyMessage) && (
          <p
            className="AmexPlatinumNightsCardRefresh__footer"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedNotifyMessage,
            }}
          />
        )}
      </div>
    </div>
  );
};
export default AmexPlatinumNightsCardRefresh;

AmexPlatinumNightsCardRefresh.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  index: PropTypes.number.isRequired,
  restaurant: PropTypes.shape({}).isRequired,
  loadNotifyModal: PropTypes.func.isRequired,
  showAuthModal: PropTypes.func.isRequired,
  comingSoonLabel: PropTypes.string.isRequired,
  bookLabel: PropTypes.string.isRequired,
  notifyLabel: PropTypes.string.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};
